import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// Migração única: renomeia chaves do localStorage de loopos_* para oplyx_*
// Necessário para sessões ativas antes do rebranding (Iter 138)
;["token", "user", "branch"].forEach(k => {
  const old = localStorage.getItem(`loopos_${k}`);
  if (old && !localStorage.getItem(`oplyx_${k}`)) {
    localStorage.setItem(`oplyx_${k}`, old);
  }
  localStorage.removeItem(`loopos_${k}`);
});
import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "sonner";
import { registerSW } from "@/lib/push";

// Static imports — critical path (always bundled)
import AuthPage from "@/pages/AuthPage";
import Landing from "@/pages/Landing";
import AppShell from "@/components/layout/AppShell";
import AdminShell from "@/components/layout/AdminShell";
import StaffShell from "@/pages/staff/StaffShell";
import SubscriptionGate from "@/components/SubscriptionGate";
import UpgradePrompt from "@/components/UpgradePrompt";
import LegalApprovalPrompt from "@/components/LegalApprovalPrompt";
import CookieConsent from "@/components/CookieConsent";
import Dashboard from "@/pages/Dashboard";
import POS from "@/pages/POS";
import Tabs from "@/pages/Tabs";

// Lazy-loaded pages — split into separate chunks
const Customers = lazy(() => import("@/pages/Customers"));
const Waitlist = lazy(() => import("@/pages/Waitlist"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const Financial = lazy(() => import("@/pages/Financial"));
const FinancialCentral = lazy(() => import("@/pages/FinancialCentral"));
const Taxes = lazy(() => import("@/pages/Taxes"));
const PayrollCharges = lazy(() => import("@/pages/PayrollCharges"));
const CashFlow = lazy(() => import("@/pages/CashFlow"));
const Team = lazy(() => import("@/pages/Team"));
const Goals = lazy(() => import("@/pages/Goals"));
const Settings = lazy(() => import("@/pages/Settings"));
const PublicProfile = lazy(() => import("@/pages/PublicProfile"));
const Fiscal = lazy(() => import("@/pages/Fiscal"));
const FiscalEmissorSettings = lazy(() => import("@/pages/FiscalEmissorSettings"));
const AdminFiscal = lazy(() => import("@/pages/AdminFiscal"));
const Purchases = lazy(() => import("@/pages/Purchases"));
const Suppliers = lazy(() => import("@/pages/Suppliers"));
const PayablesBySupplier = lazy(() => import("@/pages/PayablesBySupplier"));
const Replenishment = lazy(() => import("@/pages/Replenishment"));
const Ingredients = lazy(() => import("@/pages/Ingredients"));
const Consumption = lazy(() => import("@/pages/Consumption"));
const Stock = lazy(() => import("@/pages/Stock"));
const Production = lazy(() => import("@/pages/Production"));
const CashReports = lazy(() => import("@/pages/CashReports"));
const CashClosure = lazy(() => import("@/pages/CashClosure"));
const HourBank = lazy(() => import("@/pages/HourBank"));
const CostHistory = lazy(() => import("@/pages/CostHistory"));
const Billing = lazy(() => import("@/pages/Billing"));
const CostAnalytics = lazy(() => import("@/pages/CostAnalytics"));
const DRE = lazy(() => import("@/pages/DRE"));
const ForecastPage = lazy(() => import("@/pages/ForecastPage"));
const MenuAnalysis = lazy(() => import("@/pages/MenuAnalysis"));
const InsightsPage = lazy(() => import("@/pages/InsightsPage"));
const IntelligenceToday = lazy(() => import("@/pages/IntelligenceToday"));
const CashRadar = lazy(() => import("@/pages/CashRadar"));
const IntelligenceAsk = lazy(() => import("@/pages/IntelligenceAsk"));
const IntelligenceResults = lazy(() => import("@/pages/IntelligenceResults"));
const Implantacao = lazy(() => import("@/pages/Implantacao"));
const DREProjection = lazy(() => import("@/pages/DREProjection"));
const Reservas = lazy(() => import("@/pages/Reservas"));
const DeliveryPricing = lazy(() => import("@/pages/DeliveryPricing"));
const ValueReport = lazy(() => import("@/pages/ValueReport"));
const Accounting = lazy(() => import("@/pages/Accounting"));
const Reports = lazy(() => import("@/pages/Reports"));
const SalesReport = lazy(() => import("@/pages/SalesReport"));
const LiveOps = lazy(() => import("@/pages/LiveOps"));
const DeliveryOrders = lazy(() => import("@/pages/DeliveryOrders"));
const IFoodIntegration = lazy(() => import("@/pages/IFoodIntegration"));
const Tips = lazy(() => import("@/pages/Tips"));
const OpCosts = lazy(() => import("@/pages/OpCosts"));
const OwnerConsumption = lazy(() => import("@/pages/OwnerConsumption"));
const CancelledTabs = lazy(() => import("@/pages/CancelledTabs"));
const KDS = lazy(() => import("@/pages/KDS"));
const PrintStation = lazy(() => import("@/pages/PrintStation"));
const Reimpressao = lazy(() => import("@/pages/Reimpressao"));
const PrinterSettings = lazy(() => import("@/pages/PrinterSettings"));
const PrinterSetupGuide = lazy(() => import("@/pages/PrinterSetupGuide"));
const MyClock = lazy(() => import("@/pages/MyClock"));
const Attendance = lazy(() => import("@/pages/Attendance"));
const NFeIssue = lazy(() => import("@/pages/NFeIssue"));
const Support = lazy(() => import("@/pages/Support"));
const POSStandalone = lazy(() => import("@/pages/POSStandalone"));
const Receipt = lazy(() => import("@/pages/Receipt"));
const Reactivate = lazy(() => import("@/pages/Reactivate"));
const PublicLanding = lazy(() => import("@/pages/PublicLanding"));
const Topup = lazy(() => import("@/pages/Topup"));
const TopupSuccess = lazy(() => import("@/pages/Topup").then(m => ({ default: m.TopupSuccess })));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const PlansPage = lazy(() => import("@/pages/PlansPage"));
const SubscriptionSuccess = lazy(() => import("@/pages/SubscriptionSuccess"));
const CheckoutReturn = lazy(() => import("@/pages/CheckoutReturn"));
const BillingActivate = lazy(() => import("@/pages/BillingActivate"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const ContadorQuestionario = lazy(() => import("@/pages/ContadorQuestionario"));
const RatificacaoContrato = lazy(() => import("@/pages/RatificacaoContrato"));
const AdesaoContrato = lazy(() => import("@/pages/AdesaoContrato"));
const AutorizacaoResponsavel = lazy(() => import("@/pages/AutorizacaoResponsavel"));
const PublicReceipt = lazy(() => import("@/pages/PublicReceipt"));
const PublicOSTracking = lazy(() => import("@/pages/PublicOSTracking"));
const Welcome = lazy(() => import("@/pages/Welcome"));
const Products = lazy(() => import("@/pages/Products"));
const StaffHome = lazy(() => import("@/pages/staff/StaffHome"));
const StaffLogin = lazy(() => import("@/pages/staff/StaffLogin"));
const StaffTab = lazy(() => import("@/pages/staff/StaffTab"));

// Admin pages
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminClients = lazy(() => import("@/pages/admin/AdminClients"));
const AdminDangerZone = lazy(() => import("@/pages/admin/AdminDangerZone"));
const AdminAudit = lazy(() => import("@/pages/admin/AdminAudit"));
const AdminAlerts = lazy(() => import("@/pages/admin/AdminAlerts"));
const AdminPlans = lazy(() => import("@/pages/admin/AdminPlans"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminJuridico = lazy(() => import("@/pages/admin/AdminJuridico"));
const AdminFinance = lazy(() => import("@/pages/admin/AdminFinance"));
const AdminOps = lazy(() => import("@/pages/admin/AdminOps"));
const AdminMessages = lazy(() => import("@/pages/admin/AdminMessages"));
const AdminClientBilling = lazy(() => import("@/pages/admin/AdminClientBilling"));
const AdminOnboarding = lazy(() => import("@/pages/admin/AdminOnboarding"));
const AdminTeam = lazy(() => import("@/pages/admin/AdminTeam"));
const AdminInfraHealth = lazy(() => import("@/pages/admin/AdminInfraHealth"));
const AdminBillingPayments = lazy(() => import("@/pages/admin/AdminBillingPayments"));
const AdminCentralCobranca = lazy(() => import("@/pages/admin/AdminCentralCobranca"));
const AdminSupport = lazy(() => import("@/pages/admin/AdminSupport"));
const AdminReferrals = lazy(() => import("@/pages/admin/AdminReferrals"));

function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center bg-[#FAFAF8] dark:bg-[#0B0D0F]">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-700 dark:border-neutral-700 dark:border-t-neutral-300" />
    </div>
  );
}

const Private = ({ children }) => {
  const t = localStorage.getItem("oplyx_token");
  return t ? children : <Navigate to="/" replace />;
};

// Rota "/" decide: sem token → Landing; platform_admin → /admin; com token → AppShell
const RootGate = ({ authed }) => {
  const t = localStorage.getItem("oplyx_token");
  if (!t) {
    if (localStorage.getItem("oplyx_staff_mode")) return <Navigate to="/staff/auth" replace />;
    return <Landing />;
  }
  try {
    const u = JSON.parse(localStorage.getItem("oplyx_user") || "{}");
    if (u.is_platform_admin) return <Navigate to="/admin" replace />;
  } catch (_) {}
  return authed;
};

export default function App() {
  useEffect(() => { registerSW(); }, []);
  return (
    <>
      <UpgradePrompt />
      <LegalApprovalPrompt />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/landing" element={<Landing />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/j/:ownerId/waitlist" element={<PublicLanding mode="waitlist" />} />
            <Route path="/j/:ownerId/cashback" element={<PublicLanding mode="cashback" />} />
            <Route path="/j/:ownerId/topup" element={<Topup />} />
            <Route path="/j/:ownerId/topup/success" element={<TopupSuccess />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/r/:id" element={<PublicReceipt />} />
            <Route path="/billing/activate/:token" element={<BillingActivate />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/termos" element={<TermsOfService />} />
            <Route path="/privacidade" element={<TermsOfService />} />
            <Route path="/dpa" element={<TermsOfService />} />
            <Route path="/ia" element={<TermsOfService />} />
            <Route path="/os/:osNumber" element={<PublicOSTracking />} />
            {/* Questionário fiscal do contador — link sem login. */}
            <Route path="/contador/:token" element={<ContadorQuestionario />} />
            <Route path="/contrato/:token" element={<RatificacaoContrato />} />
            <Route path="/adesao/:token" element={<AdesaoContrato />} />
            <Route path="/autorizar/:token" element={<AutorizacaoResponsavel />} />

            {/* Iter 124 — POS Standalone (janela dedicada, sem AppShell). */}
            <Route path="/pos/standalone" element={<Private><POSStandalone /></Private>} />

            {/* Iter 129 — Cash Closure (visualização do fechamento de caixa, A4 e 80mm).
                Sem AppShell para que o layout de impressão renderize "limpo". */}
            <Route path="/financial/cash-closure/:id" element={<Private><CashClosure /></Private>} />

            {/* Iter 121 — Staff PWA dedicado (escopo /staff). */}
            <Route path="/staff" element={<StaffShell />}>
              <Route index element={<StaffHome />} />
              <Route path="auth" element={<StaffLogin />} />
              <Route path="tab/:id" element={<StaffTab />} />
            </Route>

            <Route path="/admin" element={<Private><AdminShell /></Private>}>
              <Route index element={<AdminDashboard />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="clients/:id/billing" element={<AdminClientBilling />} />
              <Route path="fiscal" element={<AdminFiscal />} />
              <Route path="alerts" element={<AdminAlerts />} />
              <Route path="plans" element={<AdminPlans />} />
              <Route path="finance" element={<AdminFinance />} />
              <Route path="ops" element={<AdminOps />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="juridico" element={<AdminJuridico />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="onboarding" element={<AdminOnboarding />} />
              <Route path="onboarding/:id" element={<AdminOnboarding />} />
              <Route path="team" element={<AdminTeam />} />
              <Route path="danger-zone" element={<AdminDangerZone />} />
              <Route path="infra-health" element={<AdminInfraHealth />} />
              <Route path="billing-payments" element={<AdminBillingPayments />} />
              <Route path="cobranca" element={<AdminCentralCobranca />} />
              <Route path="support" element={<AdminSupport />} />
              <Route path="support/:id" element={<AdminSupport />} />
              <Route path="referrals" element={<AdminReferrals />} />
            </Route>
            {/* OPLYX (Iter 78): Roastery module disabled.
                Routes commented but page imports kept to allow easy re-enable.
            <Route path="/roastery" element={<Private><RoasteryShell /></Private>}>
              <Route index element={<RoasteryDashboard />} />
              <Route path="service-orders" element={<ServiceOrders />} />
              <Route path="clients" element={<B2BClients />} />
              <Route path="green" element={<GreenInventory />} />
              <Route path="batches" element={<Batches />} />
              <Route path="roasted" element={<RoastedInventory />} />
              <Route path="products" element={<RoasteryProducts />} />
              <Route path="orders" element={<B2BOrders />} />
              <Route path="reports" element={<RoasteryReports />} />
              <Route path="team" element={<RoasteryTeam />} />
              <Route path="fiscal" element={<Fiscal />} />
              <Route path="account" element={<RoasteryAccount />} />
              <Route path="purchases" element={<Purchases dark={true} />} />
              <Route path="financial-central" element={<FinancialCentral dark={true} />} />
              <Route path="replenishment" element={<Replenishment dark={true} />} />
            </Route>
            */}
            <Route path="/receipt/:id" element={<Private><Receipt /></Private>} />
            <Route path="/welcome" element={<Private><Welcome /></Private>} />
            {/* Bloqueio de acesso agora é tratado pelo SubscriptionGate (tela com pagamento). */}
            <Route path="/plans" element={<Navigate to="/" replace />} />
            <Route path="/subscription/success" element={<Private><SubscriptionSuccess /></Private>} />
            <Route path="/billing/checkout-return" element={<Private><CheckoutReturn /></Private>} />
            <Route path="/" element={<RootGate authed={<Private><SubscriptionGate><AppShell /></SubscriptionGate></Private>} />}>
              <Route index element={<Dashboard />} />
              <Route path="pos" element={<POS />} />
              <Route path="products" element={<Products />} />
              <Route path="reports" element={<Reports />} />
              <Route path="sales-report" element={<SalesReport />} />
              <Route path="live-ops" element={<LiveOps />} />
              <Route path="delivery" element={<DeliveryOrders />} />
              <Route path="ifood-integration" element={<IFoodIntegration />} />
              <Route path="customers" element={<Customers />} />
              <Route path="waitlist" element={<Waitlist />} />
              {/* Iter 87: /inbox route removed */}
              <Route path="reactivate" element={<Reactivate />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="financial" element={<Financial />} />
              <Route path="team" element={<Team />} />
              <Route path="goals" element={<Goals />} />
              <Route path="tips" element={<Tips />} />
              <Route path="opcosts" element={<OpCosts />} />
              <Route path="tabs" element={<Tabs />} />
              <Route path="tabs/cancelled" element={<CancelledTabs />} />
              <Route path="kds" element={<KDS />} />
              <Route path="print/:destination" element={<PrintStation />} />
              <Route path="reimpressao" element={<Reimpressao />} />
              {/* As quatro ferramentas de importação vivem na Implantação. Links antigos continuam valendo. */}
              <Route path="migration" element={<Navigate to="/implantacao?tab=dados&tool=planilhas" replace />} />
              <Route path="onboarding-agent" element={<Navigate to="/implantacao?tab=dados&tool=documentos" replace />} />
              <Route path="historical-sales" element={<Navigate to="/implantacao?tab=dados&tool=historico" replace />} />
              <Route path="legacy-report" element={<Navigate to="/implantacao?tab=dados&tool=produtos" replace />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="attendance/hour-bank" element={<HourBank />} />
              <Route path="attendance/my-clock" element={<MyClock />} />
              <Route path="nfe" element={<NFeIssue />} />
              <Route path="settings" element={<Settings />} />
              <Route path="settings/public-profile" element={<PublicProfile />} />
              <Route path="settings/printers" element={<PrinterSettings />} />
              <Route path="settings/printers/guide" element={<PrinterSetupGuide />} />
              <Route path="fiscal" element={<Fiscal />} />
              <Route path="fiscal/configuracao" element={<FiscalEmissorSettings />} />
              <Route path="purchases" element={<Purchases />} />
              <Route path="financial-central" element={<FinancialCentral />} />
              <Route path="taxes" element={<Taxes />} />
              <Route path="payroll-charges" element={<PayrollCharges />} />
              <Route path="cash-flow" element={<CashFlow />} />
              <Route path="suppliers" element={<Suppliers />} />
              <Route path="payables/by-supplier" element={<PayablesBySupplier />} />
              <Route path="replenishment" element={<Replenishment />} />
              <Route path="ingredients" element={<Ingredients />} />
              <Route path="inventory/cost-history" element={<CostHistory />} />
              <Route path="consumption" element={<Consumption />} />
              <Route path="owner-consumption" element={<OwnerConsumption />} />
              <Route path="estoque" element={<Stock />} />
              <Route path="producao" element={<Production />} />
              <Route path="cash-reports" element={<CashReports />} />
              <Route path="cost-analytics" element={<CostAnalytics />} />
              {/* Análise operacional foi fundida em Relatórios gerais (mesmo
                  filtro de período/filial). Rota mantida para links salvos. */}
              <Route path="ops-analytics" element={<Navigate to="/reports" replace />} />
              <Route path="dre" element={<DRE />} />
              <Route path="forecast" element={<ForecastPage />} />
              <Route path="menu-analysis" element={<MenuAnalysis />} />
              <Route path="insights" element={<InsightsPage />} />
              <Route path="intelligence" element={<Navigate to="/intelligence/today" replace />} />
              <Route path="intelligence/today" element={<IntelligenceToday />} />
              <Route path="intelligence/cash" element={<CashRadar />} />
              <Route path="intelligence/ask" element={<IntelligenceAsk />} />
              <Route path="intelligence/results" element={<IntelligenceResults />} />
              <Route path="implantacao" element={<Implantacao />} />
              <Route path="dre-projection" element={<DREProjection />} />
              <Route path="reserves" element={<Reservas />} />
              <Route path="delivery-pricing" element={<DeliveryPricing />} />
              <Route path="value-report" element={<ValueReport />} />
              <Route path="accounting" element={<Accounting />} />
              <Route path="billing" element={<Billing />} />
              <Route path="billing/plans" element={<PlansPage />} />
              <Route path="support" element={<Support />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <CookieConsent />
    </>
  );
}
